import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core/Services/RestClientService'

export type DictionaryCountState = { status: 'loading' } | { status: 'done'; total: number } | { status: 'error' }

interface CountSource {
    resourceKey: string
    restUrl: string
}

interface CollectionMeta {
    meta?: { total?: number }
}

const LOADING: DictionaryCountState = { status: 'loading' }

/**
 * Кількість записів у кожному довіднику для огляду: один запит `restUrl?limit=1` на
 * довідник, читаємо `meta.total`. Запити паралельні, помилка одного не валить інші.
 * Кеш відповідей RestClient обходимо (`forceRefresh`), щоб після додавання запису
 * огляд показував свіже число. Уже відоме число під час оновлення лишається на екрані
 * (без скелетона), а довідник, чий запит ще летить, повторно не запитується.
 */
export class DictionaryCountsStore {
    counts = new Map<string, DictionaryCountState>()

    private inFlight = new Set<string>()

    constructor() {
        makeAutoObservable<this, 'inFlight'>(this, { inFlight: false })
    }

    get(resourceKey: string): DictionaryCountState {
        return this.counts.get(resourceKey) ?? LOADING
    }

    async load(sources: CountSource[]): Promise<void> {
        const pending = sources.filter((source) => !this.inFlight.has(source.resourceKey))
        if (pending.length === 0) return

        pending.forEach((source) => {
            this.inFlight.add(source.resourceKey)
            // Скелетон лише коли числа ще немає: відоме значення тихо оновлюється
            if (this.get(source.resourceKey).status !== 'done') this.counts.set(source.resourceKey, LOADING)
        })

        await Promise.allSettled(
            pending.map(async (source) => {
                let state: DictionaryCountState = { status: 'error' }
                try {
                    // RestClient без isOriginalResponse резолвить уже розібраний JSON, а не Response
                    const data = (await restClient.get(
                        source.restUrl,
                        { searchParams: { limit: 1 } },
                        { forceRefresh: true }
                    )) as CollectionMeta | null
                    const total = data?.meta?.total
                    if (typeof total === 'number') state = { status: 'done', total }
                } catch {
                    /* стан лишається error */
                }
                this.inFlight.delete(source.resourceKey)
                runInAction(() => this.counts.set(source.resourceKey, state))
            })
        )
    }
}

/** Один екземпляр на застосунок: огляд перечитує лічильники при кожному монтуванні */
export const dictionaryCountsStore = new DictionaryCountsStore()
